/**
 * Recent Searches — per-user localStorage bucket.
 *
 * Hardened against cross-user data leakage (same pattern as Recently
 * Viewed, Hidden Listings, Recent Categories):
 *   • ALL reads/writes require a real, non-empty userId. Empty strings,
 *     null, undefined, or the sentinel `"guest"` are REJECTED — no-ops
 *     on writes, [] on reads. There is NO unscoped fallback.
 *   • Bucket key is versioned: `wassla.recent.searches.v2.<userId>`.
 *   • `sweepLegacyRecentSearchKeys()` removes every pre-v2 bucket
 *     (shared + v1 per-user + PremiumSearchBar's separate legacy
 *     `wassla_recent_searches_v1_*` store).
 *   • `purgeAllRecentSearches()` wipes everything (v1 + v2) — called
 *     from AuthContext on logout.
 *
 * Anonymous users do NOT persist recent searches. This is intentional
 * per the product rule "anonymous users must not persist data".
 */

const MAX_RECENT_SEARCHES = 5;
const PREFIX_V2 = 'wassla.recent.searches.v2.';

// Legacy prefixes from earlier builds. Kept for one purpose only:
// sweeping them on login / logout so they cannot leak between users.
const LEGACY_SHARED_V1 = 'wassla.recent.searches.v1.guest';
const LEGACY_USER_V1 = 'wassla.recent.searches.v1.'; // includes .<uid> or .guest
const LEGACY_PSB_SHARED = 'wassla_recent_searches_v1';
const LEGACY_PSB_USER = 'wassla_recent_searches_v1_'; // PremiumSearchBar's old prefix

function isValidUserId(userId) {
  if (typeof userId !== 'string') return false;
  const trimmed = userId.trim();
  if (!trimmed) return false;
  if (trimmed === 'guest') return false;
  return true;
}

function getKey(userId) {
  return isValidUserId(userId) ? `${PREFIX_V2}${userId}` : null;
}

/** Remove every legacy bucket (shared + v1 per-user + PSB v1). */
export function sweepLegacyRecentSearchKeys() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(LEGACY_PSB_SHARED);
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k) continue;
      // Match both legacy stores:
      //   - `wassla.recent.searches.v1.<uid|guest>`
      //   - `wassla_recent_searches_v1_<uid>`
      // Do NOT touch v2 keys.
      if (
        (k.startsWith(LEGACY_USER_V1) && !k.startsWith(PREFIX_V2)) ||
        k.startsWith(LEGACY_PSB_USER)
      ) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* ignore */
  }
}

/** Remove every recent-searches bucket — used on logout. */
export function purgeAllRecentSearches() {
  if (typeof window === 'undefined') return;
  try {
    sweepLegacyRecentSearchKeys();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX_V2)) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* ignore */
  }
}

export function getRecentSearches(userId) {
  if (typeof window === 'undefined') return [];
  if (!isValidUserId(userId)) return [];
  const key = getKey(userId);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((s) => typeof s === 'string' && s).slice(0, MAX_RECENT_SEARCHES)
      : [];
  } catch {
    return [];
  }
}

export function addRecentSearch(term, userId) {
  if (typeof window === 'undefined') return;
  const query = String(term || '').trim();
  if (!query) return;
  if (!isValidUserId(userId)) return;
  const key = getKey(userId);
  if (!key) return;
  try {
    const current = getRecentSearches(userId);
    const deduped = current.filter((item) => item.toLowerCase() !== query.toLowerCase());
    const next = [query, ...deduped].slice(0, MAX_RECENT_SEARCHES);
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export function clearRecentSearches(userId) {
  if (typeof window === 'undefined') return;
  if (!isValidUserId(userId)) return;
  const key = getKey(userId);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
